import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import { ReactComponent as LogoEntire } from "../assets/img/logo.svg";
import SvgFragment from "../fragments/SvgFragment";
import { AppRoutes, SidebarTypes, ActionType } from "../interfaces/store/appStore";
import { styles } from "../styles/themes/style";
import { useAuthStore } from "../stores/auth/useAuthStore";
import { useAppStore } from "../stores/application/useAppStore";
import { useAppActions } from "../stores/application/useAppActions";
import { useAppHandlers } from "../services/actions/app/useAppHandlers";
import { useAuthHandlers } from "../services/actions/auth/useAuthHandlers";
import { useExternalHandlers } from "../services/actions/navigation/useExternalHandlers";
import Row from "./Row";
import Column from "./Column";
import Text from "./Text";
import DropdownMenu from "./dropdown/DropdownMenu";
import DropdownItem from "./dropdown/DropdownItem";
import TooltipWrapper from "./TooltipWrapper";
import { isInboxNavDisabledForStarter, shouldOpenNewInbox } from "../services/inbox/inboxNavAccess";
import { hasCampaignsAccess, shouldShowCampaignsNavItem } from "../services/campaigns/campaignsNavAccess";
import { getInboxAccessBlockedTooltip } from "../utils/syncContactsTooltips";
import { isChromeBasedBrowser, isMobileDevice } from "../utils";
import {
  CAMPAIGNS_NAV_ANNOUNCEMENT_DELAY_MS,
  CAMPAIGNS_SETUP_CALL_URL,
  dismissCampaignsNavAnnouncement,
  isProPayingUser,
  shouldShowCampaignsNavAnnouncement,
} from "../services/campaigns/campaignsNavAnnouncement";
import FeatureOnboardingBubble, { FeatureOnboardingTarget, FeatureOnboardingTextLink } from "./onboarding/FeatureOnboardingBubble";
import { useTasksHandlers } from "../services/actions/tasks/useTasksHandlers";

type NavItem = {
  name: string;
  icon: string;
  to?: string;
  extension?: string;
  badge?: string;
  inboxNav?: boolean;
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const groups: NavGroup[] = [
  {
    label: "Overview",
    items: [
      { name: "Home", icon: "sideHomeLucide", to: AppRoutes.HOME },
      { name: "Contacts", icon: "sideContactsLucide", to: AppRoutes.CONNECTIONS },
      { name: "Inbox", icon: "sideInboxLucide", inboxNav: true },
      // Pairs with the "Start a campaign" CTA above: this entry opens the campaigns
      // list, the button jumps straight into the new-campaign wizard.
      { name: "Campaigns", icon: "sideCampaignsLucide", to: AppRoutes.CAMPAIGNS },
      // Warm lead radar: people who just showed interest, read through cloud accounts.
      { name: "Signals", icon: "sideSignalsLucide", to: AppRoutes.SIGNALS, badge: "New" },
      { name: "Dashboard", icon: "sideDashboardLucide", to: AppRoutes.DASHBOARD },
    ],
  },
  {
    label: "Manage",
    items: [
      { name: "Custom feeds", icon: "sideCustomFeedLucide", to: AppRoutes.POSTS },
      { name: "Tags", icon: "sideTagsLucide", to: AppRoutes.TAGS },
      { name: "Tasks", icon: "sideTasksLucide", to: AppRoutes.TASKS },
      { name: "Templates", icon: "sideTemplatesLucide", to: AppRoutes.TEMPLATES },
      { name: "Notes", icon: "sideNotesLucide", to: AppRoutes.NOTES },
    ],
  },
  {
    label: "Tools",
    items: [
      { name: "Integrations/MCP", icon: "sideIntegrationsLucide", to: AppRoutes.INTEGRATIONS },
      { name: "LeadSearch DB", icon: "sideLeadSearchLucide", to: AppRoutes.PROSPECTS },
      { name: "CSV Data Finder", icon: "sideCsvEnrichLucide", to: AppRoutes.CSV_ENRICHMENT },
      // AI Chat is sunset for now: unused internally and a source of customer complaints.
      // Code is kept so it can be brought back; the route is commented out in AppRouter.
      // { name: "AI Chat", icon: "sideAiChatLucide", to: AppRoutes.AI_CHAT },
    ],
  },
];

type SettingsNavItem = { label: string; icon: string; to: string; key: string };

const settingsItems: SettingsNavItem[] = [
  { label: "Admin", icon: "settingsUsersRound", to: AppRoutes.SETTINGS_WORKSPACE_ADMIN, key: "workspaceAdmin" },
  { label: "Billing", icon: "settingsCreditCard", to: AppRoutes.SETTINGS_BILLING, key: "billing" },
  { label: "Conversation Tags", icon: "settingsTags", to: AppRoutes.SETTINGS_CONVERSATION_TAGS, key: "conversationTags" },
  { label: "Background update", icon: "settingsRefreshCw", to: AppRoutes.SETTINGS_EXTENSION, key: "backgroundUpdate" },
  { label: "Campaign settings", icon: "settingsSend", to: AppRoutes.SETTINGS_CAMPAIGNS, key: "campaignSettings" },
  { label: "Data management", icon: "settingsDatabase", to: AppRoutes.SETTINGS_DATA_MANAGEMENT, key: "dataManagement" },
  { label: "Custom contact properties", icon: "settingsListPlus", to: AppRoutes.CUSTOM_CONTACT_PROPERTIES, key: "customContactProperties" },
  { label: "LinkedIn sidebar", icon: "settingsPanelLeft", to: AppRoutes.SETTINGS_LINKEDIN, key: "linkedin" },
  { label: "CSV Data Finder", icon: "settingsWandSparkles", to: AppRoutes.CSV_ENRICHMENT_SETTINGS, key: "csvEnrichmentSettings" },
  { label: "Feeds", icon: "settingsLayoutGrid", to: AppRoutes.SETTINGS_FEED_MANAGEMENT, key: "feedManagement" },
  { label: "HubSpot", icon: "settingsPlug", to: AppRoutes.SETTINGS_HUBSPOT, key: "hubspotFieldMapping" },
];

const isSettingsNavItemVisible = (
  key: string,
  { workspaceOwner, workspaceAdmin, disablePremiumFeatures, workspace }: {
    workspaceOwner: boolean;
    workspaceAdmin: boolean;
    disablePremiumFeatures: boolean;
    workspace?: { hubspotEnabled?: boolean };
  }
) => {
  if (key === "workspaceAdmin" || key === "billing") {
    return workspaceOwner || workspaceAdmin;
  }
  if (key === "hubspotFieldMapping") {
    return workspace?.hubspotEnabled === true;
  }
  if (disablePremiumFeatures) {
    return key === "billing" || key === "workspaceAdmin" || key === "csvEnrichmentSettings";
  }
  return true;
};

interface ISideNavProps {
  campaignsAnnouncementEnabled?: boolean;
}

export const SideNav = ({ campaignsAnnouncementEnabled = true }: ISideNavProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    store: { workspace, userWorkspaces, user, isLoggedToLinkedin, trial, subscription, lifetime, workspaceOwner, workspaceAdmin, disablePremiumFeatures, inboxTaskId },
  } = useAuthStore();

  const visibleSettingsItems = React.useMemo(
    () =>
      settingsItems.filter((item) =>
        isSettingsNavItemVisible(item.key, { workspaceOwner, workspaceAdmin, disablePremiumFeatures, workspace })
      ),
    [workspaceOwner, workspaceAdmin, disablePremiumFeatures, workspace]
  );
  const {
    store: { navCollapsed = false, mobileNavOpen = false, extensionInstalled, extensionVersion, onboardingStep },
    dispatch,
  } = useAppStore();
  const { setNavCollapsed, setMobileNavOpen } = useAppActions();
  const { openExtension, openSidebar, setCalculateEnrichmentModalHandler } = useAppHandlers();
  const { completeSingleTask } = useTasksHandlers();
  const { changeUserWorkspace } = useAuthHandlers();
  const { learnMoreAboutCredits } = useExternalHandlers();
  const [showCreditsPopup, setShowCreditsPopup] = React.useState(false);
  const [showCampaignsAnnouncement, setShowCampaignsAnnouncement] = React.useState(false);
  const creditsHoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const campaignsAnnouncementTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const campaignsNavRef = React.useRef<HTMLDivElement | null>(null);
  const isProPayingCampaignsUser = isProPayingUser(trial, lifetime, subscription, workspace);

  const isMobile = isMobileDevice();
  const displayCollapsed = isMobile && mobileNavOpen ? false : navCollapsed;

  const closeMobileNav = React.useCallback(() => {
    if (isMobile) setMobileNavOpen(false);
  }, [isMobile, setMobileNavOpen]);

  const prevPathnameRef = React.useRef(pathname);
  React.useEffect(() => {
    if (!isMobile) return;
    if (prevPathnameRef.current === pathname) return;
    prevPathnameRef.current = pathname;
    dispatch({ type: ActionType.SET_MOBILE_NAV_OPEN, payload: false });
  }, [pathname, isMobile, dispatch]);

  const onToggle = () => {
    if (isMobile) {
      setMobileNavOpen(!mobileNavOpen);
      return;
    }
    setNavCollapsed(!navCollapsed);
  };

  const clearCampaignsAnnouncementTimer = React.useCallback(() => {
    if (campaignsAnnouncementTimerRef.current != null) {
      clearTimeout(campaignsAnnouncementTimerRef.current);
      campaignsAnnouncementTimerRef.current = null;
    }
  }, []);

  const handleDismissCampaignsAnnouncement = React.useCallback(() => {
    if (user?._id) {
      dismissCampaignsNavAnnouncement(user._id);
    }
    clearCampaignsAnnouncementTimer();
    setShowCampaignsAnnouncement(false);
  }, [user?._id, clearCampaignsAnnouncementTimer]);

  React.useEffect(() => {
    clearCampaignsAnnouncementTimer();
    setShowCampaignsAnnouncement(false);

    if (
      !campaignsAnnouncementEnabled ||
      !shouldShowCampaignsNavAnnouncement(user?._id, onboardingStep, workspace)
    ) {
      return;
    }

    campaignsAnnouncementTimerRef.current = setTimeout(() => {
      campaignsAnnouncementTimerRef.current = null;
      setShowCampaignsAnnouncement(true);
    }, CAMPAIGNS_NAV_ANNOUNCEMENT_DELAY_MS);

    return () => {
      clearCampaignsAnnouncementTimer();
    };
  }, [user?._id, onboardingStep, workspace?._id, campaignsAnnouncementEnabled, clearCampaignsAnnouncementTimer]);
  const currentWorkspaceInitials = (workspace?.name || "LD").slice(0, 2).toUpperCase();
  const campaignCtaDisabled = !hasCampaignsAccess(workspace, trial);
  const isExtensionInboxItem = (item: NavItem) => item.extension === "inbox";
  const isAppInboxItem = (item: NavItem) => item.to === AppRoutes.INBOX;
  const isInboxNavItem = (item: NavItem) => !!item.inboxNav || isExtensionInboxItem(item) || isAppInboxItem(item);
  const getNavItemKey = (item: NavItem) => item.to ?? item.extension ?? (item.inboxNav ? "inbox" : item.name);
  const shouldShowNavItem = (item: NavItem) => {
    if (item.name === "Campaigns") return shouldShowCampaignsNavItem(workspace);
    return true;
  };

  const openInboxNav = async () => {
    if (inboxTaskId) {
      await completeSingleTask(inboxTaskId, true, false);
    }
    if (shouldOpenNewInbox(workspace, lifetime)) {
      navigate(AppRoutes.INBOX);
      return;
    }
    openExtension({ query: "redirectTo=inbox" });
  };

  const isStarterLockedNavItem = (item: NavItem) =>
    (isInboxNavItem(item) && isInboxNavDisabledForStarter(workspace)) ||
    item.name === "Templates" ||
    item.to === AppRoutes.INTEGRATIONS;

  const getTooltipText = (item: NavItem) => {
    if (isInboxNavItem(item)) {
      return getInboxAccessBlockedTooltip({
        isStarterLocked: isInboxNavDisabledForStarter(workspace),
        extensionInstalled,
        extensionVersion,
        isLoggedToLinkedin,
        isSynced: user?.isSynced,
      })
    }

    if (workspace?.isStarter && (item.name === "Templates" || item.to === AppRoutes.INTEGRATIONS)) {
      return "Upgrade to Pro to unlock this feature";
    }

    if (item.to === AppRoutes.PROSPECTS && !workspace?.isBusiness && !workspace?.prospectsEnabled) {
      return "Upgrade to Growth to unlock this feature";
    }

    if (
      item.name === "AI Chat" &&
      !(workspace?.aiChatEnabled || trial || (workspace?.isBusiness && subscription?.status === "active"))
    ) {
      return "Upgrade to Growth to unlock this feature";
    }

    if (item.to === AppRoutes.CSV_ENRICHMENT && trial) {
      return "CSV enrichment available only on paid plans";
    }

    if (item.name === "Campaigns" && !hasCampaignsAccess(workspace, trial)) {
      return "Upgrade to Growth to unlock this feature";
    }

    if (item.to === AppRoutes.SIGNALS && !(workspace?.isBusiness || trial)) {
      return "Upgrade to Growth to unlock this feature";
    }

    return "";
  };

  const isDisabled = (item: NavItem) => {
    return (
      (isInboxNavItem(item) &&
        (!isChromeBasedBrowser() ||
          !isLoggedToLinkedin ||
          !extensionInstalled ||
          !user?.isSynced ||
          extensionVersion !== process.env.REACT_APP_VERSION)) ||
      (workspace?.isStarter && isStarterLockedNavItem(item)) ||
      (item.to === AppRoutes.PROSPECTS && !workspace?.isBusiness && !workspace?.prospectsEnabled) ||
      (item.name === "AI Chat" &&
        !(workspace?.aiChatEnabled || trial || (workspace?.isBusiness && subscription?.status === "active"))) ||
      (item.to === AppRoutes.CSV_ENRICHMENT && trial) ||
      (item.name === "Campaigns" && !hasCampaignsAccess(workspace, trial)) ||
      (item.to === AppRoutes.SIGNALS && !(workspace?.isBusiness || trial))
    );
  };

  const renderNavItem = (item: NavItem) => {
    const isActive = item.inboxNav
      ? pathname.startsWith(AppRoutes.INBOX)
      : !!item.to && (item.to === "/" ? pathname === "/" : pathname.startsWith(item.to));
    const itemDisabled = isDisabled(item);
    const disabledTooltip = getTooltipText(item);
    const tooltipText = disabledTooltip || (displayCollapsed ? item.name : "");

    const content = (
      <TooltipWrapper tooltipText={tooltipText}>
        <TooltipAnchor>
          <Row gap="10px" alignItems>
            <SvgFragment type={item.icon} />
            {!displayCollapsed && (
              <>
                <NavText $active={isActive} $disabled={itemDisabled}>{item.name}</NavText>
                {item.badge ? <Badge>{item.badge}</Badge> : null}
              </>
            )}
          </Row>
        </TooltipAnchor>
      </TooltipWrapper>
    );

    if (item.inboxNav) {
      return (
        <ButtonNavItem
          key={getNavItemKey(item)}
          type="button"
          onClick={() => {
            if (!itemDisabled) {
              closeMobileNav();
              openInboxNav();
            }
          }}
          $active={isActive}
          $disabled={itemDisabled}
          collapsed={displayCollapsed}
        >
          {content}
        </ButtonNavItem>
      );
    }

    if (item.to) {
      return (
        <LinkNavItem
          key={getNavItemKey(item)}
          to={item.to}
          $active={isActive}
          $disabled={itemDisabled}
          collapsed={displayCollapsed}
          onClick={(e) => {
            if (itemDisabled) e.preventDefault();
            else closeMobileNav();
          }}
        >
          {content}
        </LinkNavItem>
      );
    }

    return (
      <ButtonNavItem
        key={getNavItemKey(item)}
        type="button"
        onClick={() => {
          if (!itemDisabled && item.extension) {
            closeMobileNav();
            openExtension({ query: `redirectTo=${item.extension}` });
          }
        }}
        $active={false}
        $disabled={itemDisabled}
        collapsed={displayCollapsed}
      >
        {content}
      </ButtonNavItem>
    );
  };

  const handleGetMoreCredits = () =>
    setCalculateEnrichmentModalHandler(() => {}, () => {}, false, [], [], false, false, false, true);

  const handleCreditsMouseEnter = () => {
    if (creditsHoverTimeoutRef.current) {
      clearTimeout(creditsHoverTimeoutRef.current);
      creditsHoverTimeoutRef.current = null;
    }
    setShowCreditsPopup(true);
  };

  const handleCreditsMouseLeave = () => {
    creditsHoverTimeoutRef.current = setTimeout(() => {
      setShowCreditsPopup(false);
    }, 120);
  };

  React.useEffect(() => {
    return () => {
      if (creditsHoverTimeoutRef.current) {
        clearTimeout(creditsHoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      {isMobile && mobileNavOpen && (
        <MobileBackdrop type="button" aria-label="Close navigation" onClick={closeMobileNav} />
      )}
      <Container collapsed={displayCollapsed} $isMobile={isMobile} $mobileOpen={mobileNavOpen}>
      <HeaderSection collapsed={displayCollapsed}>
        {!displayCollapsed && (
          <LogoBox gap="8px" alignItems>
            <BrandLogo>
              <LogoEntire />
            </BrandLogo>
          </LogoBox>
        )}
        <ToggleButton $isMobile={isMobile} onClick={onToggle}>
          <SvgFragment type={displayCollapsed ? "panelLeftOpen" : "panelLeftClose"} />
        </ToggleButton>
      </HeaderSection>

      <WorkspaceSection collapsed={displayCollapsed}>
        <WorkspaceDropdown
          $collapsed={displayCollapsed}
          $padding="0 8px"
          $height="40px"
          panelWidth="240px"
          title={
            <TooltipWrapper tooltipText={workspace?.name || "Workspace"}>
              <WorkspaceTriggerContent $collapsed={displayCollapsed}>
                <WorkspaceInitial>{currentWorkspaceInitials}</WorkspaceInitial>
                {!displayCollapsed && (
                  <Text $paragraph2 $ellipsis="1" $bold>
                    {workspace?.name || "Workspace"}
                  </Text>
                )}
              </WorkspaceTriggerContent>
            </TooltipWrapper>
          }
          $backgroundColor={styles.colors.white}
          $color={styles.colors.black600}
          hideArrow={displayCollapsed}
        >
          <DropdownHeader>Switch workspace</DropdownHeader>
          {userWorkspaces?.map((w: any) => {
            const isCurrentWorkspace = (w?.workspaceId ?? w?._id) === workspace?._id;
            return (
              <WorkspaceDropdownItem
                key={w._id || w.workspaceId}
                onClickHandler={() => !isCurrentWorkspace && changeUserWorkspace(w)}
                $disabled={isCurrentWorkspace}
              >
                <DropdownRow gap="10px" alignItems>
                  <WorkspaceInitial>{w.name?.substring(0, 2).toUpperCase()}</WorkspaceInitial>
                  <TooltipWrapper tooltipText={w?.name || ""}>
                    <WorkspaceNameTooltipTarget>
                      <Text $paragraph2 $ellipsis="1" $bold={isCurrentWorkspace}>
                        {w.name}
                      </Text>
                    </WorkspaceNameTooltipTarget>
                  </TooltipWrapper>
                  {isCurrentWorkspace ? (
                    <DropdownIconSlot>
                      <SvgFragment type="checkLine" />
                    </DropdownIconSlot>
                  ) : (
                    <DropdownIconSpacer />
                  )}
                </DropdownRow>
              </WorkspaceDropdownItem>
            );
          })}
          <Divider />
          <CreateWorkspaceItem onClickHandler={() => openSidebar(SidebarTypes.WORKSPACE_CREATE)}>
            <DropdownRow gap="10px" alignItems justifyCenter>
              <CreateWorkspaceIconSlot>
                <SvgFragment type="plusLine" />
              </CreateWorkspaceIconSlot>
              <CreateWorkspaceLabel $bold>Create new workspace</CreateWorkspaceLabel>
            </DropdownRow>
          </CreateWorkspaceItem>
        </WorkspaceDropdown>
      </WorkspaceSection>

      {shouldShowCampaignsNavItem(workspace) && (
        <CampaignCtaSection collapsed={displayCollapsed}>
          {/* The "New — Campaigns" onboarding bubble used to hang off the Campaigns nav
              item; it now points at this button, which replaced it. */}
          <FeatureOnboardingTarget ref={campaignsNavRef} $highlight={showCampaignsAnnouncement}>
            <TooltipWrapper
              tooltipText={
                campaignCtaDisabled
                  ? 'Upgrade to Growth to unlock this feature'
                  : displayCollapsed ? 'Start a campaign' : ''
              }
            >
              <TooltipAnchor>
                <CampaignCtaButton
                  type="button"
                  collapsed={displayCollapsed}
                  $disabled={campaignCtaDisabled}
                  onClick={() => {
                    // Straight into the new-campaign wizard — the Campaigns nav entry
                    // below is the way to the list.
                    if (campaignCtaDisabled) return;
                    closeMobileNav();
                    navigate(AppRoutes.CAMPAIGN_NEW);
                  }}
                >
                  <PaperPlaneIcon />
                  {!displayCollapsed && <span>Start a campaign</span>}
                </CampaignCtaButton>
              </TooltipAnchor>
            </TooltipWrapper>
            {showCampaignsAnnouncement && (
              <FeatureOnboardingBubble
                anchorRef={campaignsNavRef}
                repositionKey={displayCollapsed}
                placement="right"
                kicker="New"
                title="Say hello to Campaigns"
                text={
                  isProPayingCampaignsUser ? (
                    <>
                      <FeatureOnboardingTextLink
                        href={CAMPAIGNS_SETUP_CALL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Book
                      </FeatureOnboardingTextLink>
                      {' '}a call with our team and get set up with Campaigns.
                    </>
                  ) : (
                    'Start your first multi-step campaign to connect and engage with your leads on LinkedIn.'
                  )
                }
                primaryLabel="Got it"
                onPrimary={handleDismissCampaignsAnnouncement}
              />
            )}
          </FeatureOnboardingTarget>
        </CampaignCtaSection>
      )}

      <NavScroll>
        {groups.map((group) => (
          <NavSection key={group.label}>
            {!displayCollapsed && group.label !== "Overview" && <GroupLabel $bold>{group.label}</GroupLabel>}
            <Column gap="2px">{group.items.filter(shouldShowNavItem).map(renderNavItem)}</Column>
          </NavSection>
        ))}
      </NavScroll>

      <FooterSection>
        <SettingsDropdown
          $padding="0 5px"
          $height="40px"
          panelWidth={isMobile ? "100%" : "260px"}
          drop={isMobile ? "up" : "end"}
          align={isMobile ? "start" : "end"}
          $isMobile={isMobile}
          popperConfig={
            isMobile
              ? {
                  strategy: "absolute",
                  modifiers: [{ name: "flip", enabled: false }],
                }
              : undefined
          }
          tooltipMessage={displayCollapsed ? "Settings" : ""}
          title={!displayCollapsed && <SettingsTriggerLabel $bold>Settings</SettingsTriggerLabel>}
          SVGtype="settings"
          $backgroundColor="transparent"
          $color={styles.colors.black600}
          hideArrow={displayCollapsed}
        >
          {visibleSettingsItems.map((item) => {
            const isActive = pathname.startsWith(item.to);
            const isDisabled = item.to === AppRoutes.CSV_ENRICHMENT_SETTINGS && trial;
            const tooltipText = isDisabled ? "CSV enrichment available only on paid plans" : "";

            return (
            <SettingsDropdownItem key={item.label} onClickHandler={() => {
              if (isDisabled) return;
              closeMobileNav();
              navigate(item.to);
            }} $active={isActive} $disabled={isDisabled}>
              <TooltipWrapper tooltipText={tooltipText} container={document.body}>
                <TooltipAnchor>
                  <DropdownRow gap="10px" alignItems>
                    <DropdownIconSlot>
                      <SvgFragment type={item.icon} />
                    </DropdownIconSlot>
                    <DropdownLabel>{item.label}</DropdownLabel>
                  </DropdownRow>
                </TooltipAnchor>
              </TooltipWrapper>
            </SettingsDropdownItem>
          )})}
        </SettingsDropdown>

        <Divider />

        <CreditsPopupAnchor onMouseEnter={handleCreditsMouseEnter} onMouseLeave={handleCreditsMouseLeave}>
          {displayCollapsed ? (
            <TooltipAnchor>
              <CreditsBox collapsed={displayCollapsed} alignItems gap="10px">
                <SvgFragment type="coins" />
              </CreditsBox>
            </TooltipAnchor>
          ) : (
            <CreditsBox collapsed={displayCollapsed} alignItems gap="10px">
              <SvgFragment type="coins" />
              <Text $bold>Credits: {workspace?.enrichmentCredits || 0}</Text>
            </CreditsBox>
          )}
          {showCreditsPopup && (
            <CreditsPopup gap="10px" onMouseEnter={handleCreditsMouseEnter} onMouseLeave={handleCreditsMouseLeave}>
              <CreditsPopupSection as={Row} spaceBetween>
                <Text $bold>Credits left</Text>
                <Text>{workspace?.enrichmentCredits || 0}</Text>
              </CreditsPopupSection>
              <CreditsPopupSection as={Column} gap="5px" $highlight>
                {workspace?.enrichmentCredits <= 0 && (
                  <Text $paragraph3>You have no credits left. Get more credits to continue enrichment.</Text>
                )}
                {workspace?.enrichmentCredits > 0 && workspace?.enrichmentCredits < 15 && (
                  <Text $paragraph3>You are on a roll. Get more credits and keep up the momentum.</Text>
                )}
                <CreditsPopupLink onClick={learnMoreAboutCredits}>Learn more about credits.</CreditsPopupLink>
              </CreditsPopupSection>
              <CreditsUsageBox gap="8px">
                <Text $bold $lighter>Credit usage:</Text>
                <Column gap="4px">
                  <Row spaceBetween>
                    <Text>Per found Business email</Text>
                    <Text>1 credit</Text>
                  </Row>
                  <Row spaceBetween>
                    <Text>Per found Personal email</Text>
                    <Text>3 credits</Text>
                  </Row>
                  <Row spaceBetween>
                    <Text>Per found Phone number</Text>
                    <Text>10 credits</Text>
                  </Row>
                </Column>
              </CreditsUsageBox>
            </CreditsPopup>
          )}
        </CreditsPopupAnchor>

        {!displayCollapsed ? (
          <UpgradeButton $bold onClick={handleGetMoreCredits}>Get more credits</UpgradeButton>
        ) : (
          <TooltipWrapper tooltipText="Get more credits">
            <UpgradeButtonSmall onClick={handleGetMoreCredits}>
              <SvgFragment type="plus" />
            </UpgradeButtonSmall>
          </TooltipWrapper>
        )}
      </FooterSection>
    </Container>
    </>
  );
};

const MobileBackdrop = styled.button`
  position: fixed;
  inset: 0;
  z-index: 1006;
  margin: 0;
  padding: 0;
  border: none;
  background: rgba(16, 24, 40, 0.45);
  cursor: pointer;
`;

const Container = styled.aside<{ collapsed: boolean; $isMobile?: boolean; $mobileOpen?: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "64px" : "196px")};
  min-width: ${({ collapsed }) => (collapsed ? "64px" : "196px")};
  height: 100%;
  background: ${styles.colors.white};
  border-right: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease, transform 0.25s ease;
  z-index: 102;

  ${({ $isMobile, $mobileOpen }) =>
    $isMobile &&
    `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 100vw;
    min-width: 0;
    height: 100%;
    height: 100dvh;
    z-index: 1007;
    transform: translateX(${$mobileOpen ? "0" : "-100%"});
    border-right: none;
    box-shadow: ${$mobileOpen ? "0 0 24px rgba(0, 0, 0, 0.12)" : "none"};
  `}
`;

const HeaderSection = styled.div<{ collapsed: boolean }>`
  height: 64px;
  padding: 0 12px;
  display: flex;
  align-items: center;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "space-between")};
  border-bottom: 1px solid #eef2f7;
`;

const LogoBox = styled(Row)`
  min-width: 0;
`;

const TooltipAnchor = styled.span`
  display: inline-flex;
  width: 100%;
`;

const BrandLogo = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 0;
  user-select: none;

  & > svg {
    height: 26px;
    width: auto;
  }
`;

const ToggleButton = styled.button<{ $isMobile?: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  width: ${({ $isMobile }) => ($isMobile ? "40px" : "32px")};
  height: ${({ $isMobile }) => ($isMobile ? "40px" : "32px")};
  padding: 0;
  border-radius: 8px;
  color: ${styles.colors.black300};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #f3f6fb;
  }

  .svg {
    width: ${({ $isMobile }) => ($isMobile ? "20px" : "16px")};
    height: ${({ $isMobile }) => ($isMobile ? "20px" : "16px")};
  }
`;

const WorkspaceSection = styled.div<{ collapsed: boolean }>`
  padding: 12px;
  display: flex;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "stretch")};
`;

const WorkspaceDropdown = styled(DropdownMenu)<{ $collapsed?: boolean }>`
  width: 100%;
  max-width: 220px;
  margin: 0 auto;

  .dropdown-toggle {
    width: 100%;
    height: 40px;
    border: 1px solid #e6ebf3 !important;
    border-radius: 10px !important;
    background: ${styles.colors.white} !important;
    color: ${styles.colors.black600} !important;
    padding: ${({ $collapsed }) => ($collapsed ? "0 6px !important" : "0 8px !important")};
    font-size: 13px !important;
    font-weight: 700 !important;
    gap: 6px !important;
    justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  }

  .dropdownWrapperReference {
    width: 100%;
    min-width: 0;
    border: none !important;
    background: transparent !important;
    padding: 0 !important;
    border-radius: 0 !important;
    gap: 6px !important;
    justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  }

  .dropdownWrapperReference > span {
    min-width: 0;
  }

  .actions-dropdown + div {
    border-radius: 12px !important;
    border: 1px solid #e6ebf3 !important;
    box-shadow: 0 10px 28px rgba(25, 40, 72, 0.12) !important;
    padding: 8px !important;
  }
`;

const WorkspaceDropdownItem = styled(DropdownItem)`
  border-radius: 10px;
`;

const CreateWorkspaceItem = styled(WorkspaceDropdownItem)`
  border: 1px solid #e6ebf3;
  margin-top: 6px;

  &:hover {
    border-color: #d6deec;
    background: #f8faff;
  }
`;

const CreateWorkspaceIconSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  min-width: 16px;
  height: 16px;
  color: ${styles.colors.black600};

  .svg {
    width: 14px !important;
    height: 14px !important;
  }
`;

const CreateWorkspaceLabel = styled(Text)`
  color: ${styles.colors.black600};
  font-size: 13px;
`;

const WorkspaceTriggerContent = styled.div<{ $collapsed?: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? "auto" : "100%")};
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  gap: 8px;
  margin: ${({ $collapsed }) => ($collapsed ? "0 auto" : "0")};
`;

/* Deliberately a light lavender rather than the brand purple: the workspace badge
   sits directly above the "Start a campaign" CTA and must not compete with it. */
const WorkspaceInitial = styled.div`
  width: 28px;
  min-width: 28px;
  height: 28px;
  background-color: ${styles.colors.primary200};
  color: ${styles.colors.primary600};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 11px;
  font-weight: bold;
`;

/* Inlined rather than pulled from SvgFragment: the shared `send` glyph hard-codes a
   black stroke, and this one has to sit white on the purple CTA. */
const PaperPlaneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.89 11.5H6.24" />
    <path d="M6.17 11.76L4.2 17.86c-.16.48-.23.72-.18.87.05.13.16.23.28.26.15.05.37-.05.82-.26l14.12-6.58c.44-.2.65-.31.72-.45a.4.4 0 0 0 0-.34c-.07-.14-.28-.24-.72-.45L5.11 4.28c-.44-.21-.67-.31-.81-.27a.36.36 0 0 0-.28.26c-.06.15.01.39.17.87l1.98 6.18c.03.08.04.12.05.16v.09c0 .04-.02.08-.05.16Z" />
  </svg>
);

const CampaignCtaSection = styled.div<{ collapsed: boolean }>`
  padding: 0 12px 12px;
  display: flex;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "stretch")};
`;

const CampaignCtaButton = styled.button<{ collapsed: boolean; $disabled: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "34px" : "100%")};
  min-height: 34px;
  height: 34px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: ${({ collapsed }) => (collapsed ? "0" : "0 12px")};
  border: none;
  border-radius: 10px;
  background: ${({ $disabled }) => ($disabled ? styles.colors.black200 : styles.colors.primary600)};
  color: ${styles.colors.white};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  box-shadow: ${({ $disabled }) => ($disabled ? "none" : "0 4px 12px rgba(89, 100, 255, 0.28)")};
  transition: background 0.15s ease, box-shadow 0.15s ease;

  span {
    font-family: NunitoSans;
    font-size: 13.5px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
  }

  svg { flex-shrink: 0; width: 15px; height: 15px; }

  &:hover {
    background: ${({ $disabled }) => ($disabled ? styles.colors.black200 : styles.colors.primary500)};
  }
`;

const NavScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px 12px 10px;

  scrollbar-width: thin;
  scrollbar-color: #e8eaed transparent;

  &::-webkit-scrollbar { width: 4px; background: transparent; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #e8eaed; border-radius: 2px; }
`;

const NavSection = styled.div`
  margin-bottom: 18px;
`;

const GroupLabel = styled(Text)`
  font-size: 10px;
  text-transform: uppercase;
  color: ${styles.colors.black300};
  padding: 0 12px;
  margin-bottom: 6px;
  letter-spacing: 0.08em;
`;

const navItemStyles = css<{ $active: boolean; collapsed: boolean; $disabled: boolean }>`
  text-decoration: none;
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 8px 10px;
  border-radius: 10px;
  background-color: ${({ $active, $disabled }) => ($disabled ? "transparent" : ($active ? "#eff3ff" : "transparent"))};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  border: none;
  width: 100%;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
  opacity: 1;

  &:hover {
    background-color: ${({ $active, $disabled }) => ($disabled ? "transparent" : ($active ? "#eff3ff" : "#f5f7fb"))};

    .path {
      stroke: ${({ $disabled }) => ($disabled ? styles.colors.disabledState : styles.colors.primary600)};
      fill: none;
    }

    .path-fill {
      fill: ${({ $disabled }) => ($disabled ? styles.colors.disabledState : styles.colors.primary600)};
      stroke: none;
    }

    span {
      color: ${({ $disabled }) => ($disabled ? styles.colors.disabledState : styles.colors.primary600)};
    }
  }

  .svg {
    width: 17px;
    height: 17px;

    .path {
      stroke: ${({ $active, $disabled }) => ($disabled ? styles.colors.disabledState : ($active ? styles.colors.primary600 : "#6a7181"))};
      fill: none;
    }

    .path-fill {
      fill: ${({ $active, $disabled }) => ($disabled ? styles.colors.disabledState : ($active ? styles.colors.primary600 : "#6a7181"))};
      stroke: none;
    }
  }
`;

const LinkNavItem = styled(Link)<{ $active: boolean; collapsed: boolean; $disabled: boolean }>`
  ${navItemStyles}
`;

const ButtonNavItem = styled.button<{ $active: boolean; collapsed: boolean; $disabled: boolean }>`
  ${navItemStyles}
`;

const NavText = styled(Text)<{ $active: boolean; $disabled: boolean }>`
  color: ${({ $active, $disabled }) => ($disabled ? styles.colors.disabledState : ($active ? styles.colors.primary600 : styles.colors.black600))};
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? "700" : "500")};
`;

const Badge = styled.span`
  color: ${styles.colors.primary600};
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 800;
  margin-left: auto;
`;

const FooterSection = styled.div`
  padding: 8px 12px 12px;
  border-top: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #eef2f7;
  margin: 4px 0;
`;

const SettingsDropdown = styled(DropdownMenu)<{ $isMobile?: boolean }>`
  width: 100%;
  ${({ $isMobile }) => $isMobile && `position: relative;`}

  .dropdown-toggle, .dropdownWrapperReference {
    width: 100%;
    min-height: 36px;
    border-radius: 10px !important;
    color: ${styles.colors.black600} !important;
    padding: 0 5px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    gap: 10px !important;
  }

  .dropdown-toggle > .svg,
  .dropdownWrapperReference > .svg {
    width: 16px !important;
    height: 16px !important;
    min-width: 16px;
    color: ${styles.colors.black300} !important;
  }

  .dropdown-toggle > .svg .path,
  .dropdownWrapperReference > .svg .path {
    stroke: #6a7181 !important;
    fill: none !important;
  }

  .dropdown-toggle > .svg .path-fill,
  .dropdownWrapperReference > .svg .path-fill {
    fill: #6a7181 !important;
    stroke: none !important;
  }

  .dropdown-toggle:hover, .dropdownWrapperReference:hover {
    background: #f5f7fb !important;
    color: ${styles.colors.black600} !important;
  }

  .dropdown-toggle:hover > .svg .path,
  .dropdownWrapperReference:hover > .svg .path {
    stroke: #6a7181 !important;
    fill: none !important;
  }

  .dropdown-toggle:hover > .svg .path-fill,
  .dropdownWrapperReference:hover > .svg .path-fill {
    fill: #6a7181 !important;
    stroke: none !important;
  }

  .actions-dropdown + div {
    border-radius: 12px !important;
    border: 1px solid #e6ebf3 !important;
    box-shadow: 0 10px 28px rgba(25, 40, 72, 0.12) !important;
    padding: 8px !important;
    ${({ $isMobile }) =>
      $isMobile &&
      `
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      left: 0 !important;
      right: auto !important;
      transform: none !important;
      margin-bottom: 8px;
    `}
  }
`;

const SettingsDropdownItem = styled(DropdownItem)<{ $active?: boolean; $disabled?: boolean }>`
  border-radius: 10px;
  min-height: 38px;
  padding: 8px 10px !important;
  background: ${({ $active, $disabled }) => ($disabled ? "transparent" : ($active ? "#eef3ff" : "transparent"))};
  color: ${({ $active, $disabled }) => ($disabled ? styles.colors.disabledState : ($active ? styles.colors.primary600 : styles.colors.black600))} !important;
  font-size: 14px;
  font-weight: 500;

  .svg .path {
    stroke: ${({ $disabled }) => ($disabled ? styles.colors.disabledState : styles.colors.primary600)} !important;
    fill: none !important;
  }

  .svg .path-fill {
    fill: ${({ $disabled }) => ($disabled ? styles.colors.disabledState : styles.colors.primary600)} !important;
    stroke: none !important;
  }

  &:hover,
  &:focus {
    background: ${({ $active, $disabled }) => ($disabled ? "transparent" : ($active ? "#f5f7fb" : "transparent"))} !important;
    color: ${({ $active, $disabled }) => ($disabled ? styles.colors.disabledState : ($active ? styles.colors.primary600 : styles.colors.black600))} !important;
  }

  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
`;

const SettingsTriggerLabel = styled(Text)`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const DropdownRow = styled(Row)`
  width: 100%;
  min-width: 0;
`;

const WorkspaceNameTooltipTarget = styled.div`
  flex: 1;
  min-width: 0;
`;

const DropdownIconSlot = styled.span`
  width: 16px;
  min-width: 16px;
  height: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

const DropdownIconSpacer = styled.span`
  width: 16px;
  min-width: 16px;
  height: 16px;
`;

const DropdownLabel = styled(Text)`
  width: 180px;
  max-width: 180px;
  font-size: 14px;
  font-weight: 500;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DropdownHeader = styled(Text)`
  font-size: 11px;
  text-transform: uppercase;
  color: ${styles.colors.black300};
  letter-spacing: 0.06em;
  padding: 4px 10px 8px;
`;

const CreditsBox = styled(Row)<{ collapsed: boolean }>`
  padding: 2px 8px 0;
  justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};

  .svg {
    width: 16px;
    height: 16px;

    .path {
      stroke: ${styles.colors.yellow600};
      fill: none;
    }
  }
`;

const CreditsPopupAnchor = styled.div`
  position: relative;
  cursor: pointer;
`;

const CreditsPopup = styled(Column)`
  position: absolute;
  bottom: calc(100% + 6px);
  left: calc(100% + 8px);
  transform: translate(-40px, 20px);
  z-index: 1200;
  width: 300px;
  background-color: ${styles.colors.white};
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 12px;
`;

const CreditsPopupSection = styled.section<{ $highlight?: boolean }>`
  background-color: ${({ $highlight }) => ($highlight ? styles.colors.yellow300 : styles.colors.black100)};
  border-radius: 8px;
  padding: 8px;
`;

const CreditsUsageBox = styled(Column)`
  margin-top: 2px;
`;

const CreditsPopupLink = styled.span`
  cursor: pointer;
  font-family: NunitoSans;
  font-size: 12px;
  line-height: 18px;
  text-decoration: underline;
  color: ${styles.colors.black600};

  &:hover {
    color: ${styles.colors.primary600};
  }
`;

const UpgradeButton = styled.button<{ $bold?: boolean }>`
  background-color: ${styles.colors.yellow600};
  color: ${styles.colors.black600};
  border: none;
  border-radius: 10px;
  min-height: 34px;
  cursor: pointer;
  font-weight: ${({ $bold }) => ($bold ? "700" : "600")};
  font-size: 13px;
`;

const UpgradeButtonSmall = styled.button`
  width: 36px;
  height: 36px;
  margin: 0 auto;
  border: none;
  border-radius: 10px;
  background: ${styles.colors.yellow600};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

